import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export function Footer() {
  const { t } = useTranslation()

  const productLinks = [
    { label: t('footer.product.features'), href: '/#features' },
    { label: t('footer.product.howItWorks'), href: '/#faq' },
    { label: t('footer.product.download'), href: '/#download' },
  ]

  const companyLinks = [
    { label: t('footer.company.about'), href: '/about' },
    { label: t('footer.company.blog'), href: '/blog' },
    { label: t('footer.company.contact'), href: '/contact' },
  ]

  const legalLinks = [
    { label: t('footer.legal.terms'), href: '/terms' },
    { label: t('footer.legal.privacy'), href: '/privacy' },
    { label: t('footer.legal.cookies') || 'Cookies', href: '/cookies' },
    { label: t('footer.legal.refunds'), href: '/refunds' },
    { label: t('footer.legal.disputes'), href: '/disputes' },
  ]

  return (
    <footer className="bg-wine-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ═══ Top section — Newsletter + Brand ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12 lg:gap-16 pt-16 md:pt-20 pb-12 border-b border-white/[0.06]">
          {/* Left — Brand + newsletter */}
          <div>
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-xl overflow-hidden shadow-lg shadow-black/20">
                <img src="/logo_mark.png" alt="SAKK" className="h-full w-full object-cover" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white">{t('footer.brandFull')}</h3>
                <p className="text-[10px] text-white/60 font-mono tracking-[0.2em] uppercase">{t('footer.brandSub')}</p>
              </div>
            </div>

            <p className="text-sm text-white/55 leading-relaxed max-w-md mb-8">
              {t('footer.description')}
            </p>

            {/* Newsletter */}
            <div className="max-w-md">
              <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
                {t('footer.newsletter.title')}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white/[0.06] border border-white/10 focus-within:border-gold-500/30 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-ink-hint shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                  </svg>
                  <input
                    type="email"
                    placeholder={t('footer.newsletter.emailPlaceholder')}
                    className="flex-1 bg-transparent text-sm text-white/70 placeholder:text-white/20 outline-none"
                  />
                </div>
                <button className="w-full sm:w-auto px-5 py-3 rounded-xl bg-gold-500 text-wine-950 text-sm font-bold hover:bg-gold-400 transition-colors shadow-lg shadow-gold-500/15 shrink-0">
                  {t('footer.newsletter.subscribe')}
                </button>
              </div>
            </div>
          </div>

          {/* Right — Link columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            {/* Product */}
            <div>
              <h4 className="text-sm font-bold text-white mb-5">{t('footer.product.title')}</h4>
              <ul className="space-y-3">
                {productLinks.map((link, i) => (
                  <li key={i}>
                    <Link to={link.href} className="text-sm text-white/60 hover:text-gold-400 transition-colors duration-200">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-bold text-white mb-5">{t('footer.company.title')}</h4>
              <ul className="space-y-3">
                {companyLinks.map((link, i) => (
                  <li key={i}>
                    <Link to={link.href} className="text-sm text-white/60 hover:text-gold-400 transition-colors duration-200">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-bold text-white mb-5">{t('footer.legal.title')}</h4>
              <ul className="space-y-3">
                {legalLinks.map((link, i) => (
                  <li key={i}>
                    <Link to={link.href} className="text-sm text-white/60 hover:text-gold-400 transition-colors duration-200">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ═══ Bottom bar ═══ */}
        <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-5">
          {/* Copyright */}
          <p className="text-xs text-white/50">
            &copy; {new Date().getFullYear()} SAKK. {t('footer.copyright')}
          </p>

          {/* Social + badges */}
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {/* Social icons */}
            {[
              { label: 'X', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
              { label: 'IG', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
              { label: 'TG', path: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' },
            ].map((social, i) => (
              <a
                key={i}
                href="#"
                className="h-9 w-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-gold-400 hover:bg-white/[0.1] transition-all"
                aria-label={social.label}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d={social.path} />
                </svg>
              </a>
            ))}

            <div className="w-px h-5 bg-white/10 mx-1" />

            {/* Badges */}
            {[
              { label: t('footer.badges.pci') },
              { label: t('footer.badges.stripe') },
              { label: t('footer.badges.encryption') },
            ].map((badge, i) => (
              <span key={i} className="px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] text-[10px] font-bold text-white/50 uppercase tracking-wider">
                {badge.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
